import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { FiX } from 'react-icons/fi';
import { END_POINT, getJwtToken } from '../../../Redux/AdminReducer/action';
import RightWrapper from '../../../Components/CommonComponents/ComponentWrapper';
import CertificateDocument from './CertificateDocument';
import ShareLinkPanel from '../../../Components/CommonComponents/ShareLinkPanel';
import ModalPortal, { MODAL_Z } from '../../../Components/CommonComponents/ModalPortal';
import {
  CLINIC_DEFAULTS,
  certificateTemplates,
  templateKeys,
  buildCertificateForm,
  certificateToForm,
  formatDate,
  FIELD_LABELS,
  DATE_FIELDS,
} from './certificateTemplates';

const authHeaders = () => ({ headers: { Authorization: getJwtToken() } });

const EditField = ({ label, value, onChange, type = 'text', className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
    />
  </div>
);

const Modal = ({ title, onClose, children, footer, width = 'max-w-6xl' }) => (
  <ModalPortal onClose={onClose}>
    <div className={`fixed inset-0 ${MODAL_Z} flex items-center justify-center overflow-y-auto p-4`}>
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative bg-white rounded-2xl shadow-2xl ${width} w-full max-h-[90vh] flex flex-col overflow-hidden`}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <h3 className="font-semibold text-slate-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">{footer}</div>}
      </div>
    </div>
  </ModalPortal>
);

const Certificates = () => {
  // ---------------- issued certificates table ----------------
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // ---------------- clinic letterhead defaults ----------------
  const [clinic, setClinic] = useState(CLINIC_DEFAULTS);

  // ---------------- generate flow (patient first, then template) ----------------
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerStep, setPickerStep] = useState('patient');
  const [pickedCustomer, setPickedCustomer] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);

  // ---------------- editor / view ----------------
  const [form, setForm] = useState(null);
  const [editorMode, setEditorMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewForm, setViewForm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ---------------- share link ----------------
  const [shareTarget, setShareTarget] = useState(null);

  // Off-screen node used to render a certificate for download from the table
  const [downloadForm, setDownloadForm] = useState(null);
  const downloadRef = useRef(null);

  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: rowsPerPage });
      if (search) params.append('search', search);
      if (typeFilter) params.append('type', typeFilter);

      const { data } = await axios.get(`${END_POINT}/certificate?${params.toString()}`, authHeaders());
      setCertificates(data?.data || []);
      setTotalPages(data?.totalPages || 1);
      setTotal(data?.total || 0);
    } catch (error) {
      console.log('Error fetching certificates:', error);
      toast.error(error?.response?.data?.message || 'Could not load certificates');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, typeFilter]);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  // Pull the clinic letterhead so certificates are not hard-coded to one clinic
  useEffect(() => {
    axios
      .get(`${END_POINT}/clinic-profile`, authHeaders())
      .then(({ data }) => {
        const profile = data?.data;
        if (!profile) return;
        setClinic((prev) => ({
          ...prev,
          clinicName: profile.ClinicName || prev.clinicName,
          doctorName: profile.DoctorName || prev.doctorName,
          mobile: profile.ClinicContact || prev.mobile,
        }));
      })
      .catch((error) => console.log('Clinic profile not available:', error?.message));
  }, []);

  // Patient lookup inside the generate flow (debounced)
  useEffect(() => {
    if (!pickerOpen) return undefined;
    setPatientsLoading(true);
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ limit: 20, page: 1 });
      if (patientSearch) params.append('search', patientSearch);

      axios
        .get(`${END_POINT}/customer/list/dashboard?${params.toString()}`, authHeaders())
        .then(({ data }) => setPatients(Array.isArray(data) ? data : []))
        .catch((error) => {
          console.log('Error fetching patients:', error);
          toast.error('Could not load patients');
        })
        .finally(() => setPatientsLoading(false));
    }, 300);

    return () => clearTimeout(timer);
  }, [patientSearch, pickerOpen]);

  const setField = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  // ---------------- generate ----------------
  const startGenerate = () => {
    setPickedCustomer(null);
    setPatientSearch('');
    setPickerStep('patient');
    setPickerOpen(true);
  };

  const choosePatient = (customer) => {
    setPickedCustomer(customer);
    setPickerStep('type');
  };

  const chooseTemplate = (type) => {
    setForm(buildCertificateForm(pickedCustomer, type, clinic));
    setEditorMode('create');
    setEditingId(null);
    setPickerOpen(false);
  };

  const regenerateBody = () => {
    if (!form?.type) return;
    setForm((prev) => ({
      ...prev,
      bodyText: certificateTemplates[prev.type].body(prev).trim(),
    }));
  };

  // ---------------- persistence ----------------
  const buildPayload = (data) => ({
    patientId: data.patientId,
    patientName: data.patientName,
    patientMobile: data.patientMobile,
    type: data.type,
    title: data.title,
    clinicName: data.clinicName,
    doctorName: data.doctorName,
    qualification: data.qualification,
    mobile: data.mobile,
    ext: data.ext,
    timings: data.timings,
    address: data.address,
    bodyText: data.bodyText,
    fromDate: data.fromDate || null,
    toDate: data.toDate || null,
    footerDate: data.footerDate || null,
    fees: data.fees,
    disease: data.disease,
    duration: data.duration,
    place: data.place,
  });

  const saveCertificate = async () => {
    if (!form) return null;
    setSaving(true);
    try {
      const payload = buildPayload(form);
      const { data } =
        editorMode === 'edit'
          ? await axios.put(`${END_POINT}/certificate/${editingId}`, payload, authHeaders())
          : await axios.post(`${END_POINT}/certificate`, payload, authHeaders());

      toast.success(editorMode === 'edit' ? 'Certificate updated' : 'Certificate issued');
      await fetchCertificates();
      return data?.data;
    } catch (error) {
      console.log('Error saving certificate:', error);
      toast.error(error?.response?.data?.message || 'Could not save certificate');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const saved = await saveCertificate();
    if (saved) setForm(null);
  };

  const handleSaveAndDownload = async () => {
    const saved = await saveCertificate();
    if (!saved) return;
    setForm(null);
    setDownloadForm(certificateToForm(saved));
  };

  const openEdit = (certificate) => {
    setForm(certificateToForm(certificate));
    setEditorMode('edit');
    setEditingId(certificate._id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${END_POINT}/certificate/${deleteTarget._id}`, authHeaders());
      toast.success('Certificate deleted');
      setDeleteTarget(null);
      // Step back a page when the last row on this page is removed
      if (certificates.length === 1 && page > 1) setPage((prev) => prev - 1);
      else fetchCertificates();
    } catch (error) {
      console.log('Error deleting certificate:', error);
      toast.error(error?.response?.data?.message || 'Could not delete certificate');
    }
  };

  // ---------------- download ----------------
  const captureAndSave = useCallback(async (node, fileName) => {
    if (!node) return;
    try {
      const canvas = await html2canvas(node, { scale: 2, useCORS: true });
      await new Promise((resolve) =>
        canvas.toBlob((blob) => {
          if (blob) saveAs(blob, fileName);
          resolve();
        })
      );
    } catch (error) {
      console.log('Error generating certificate image:', error);
      toast.error('Could not generate the certificate image');
    }
  }, []);

  const fileNameFor = (data) =>
    `${data.certificateNo || 'certificate'}-${(data.patientName || 'patient').replace(/\s+/g, '-')}.png`;

  // Renders the hidden document, waits a frame for images, then captures it
  useEffect(() => {
    if (!downloadForm) return;
    const timer = setTimeout(async () => {
      await captureAndSave(downloadRef.current, fileNameFor(downloadForm));
      setDownloadForm(null);
    }, 400);
    return () => clearTimeout(timer);
  }, [downloadForm, captureAndSave]);

  const isEditorOpen = Boolean(form);

  return (
    <RightWrapper>
      <div className="p-6 w-full">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Issued Certificates</h1>
            <p className="text-sm text-gray-500">{total} certificate(s) issued</p>
          </div>
          <button
            onClick={startGenerate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-semibold shadow-sm"
          >
            + Generate Certificate
          </button>
        </div>

        {/* ================= FILTERS ================= */}
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Search</label>
            <input
              type="text"
              value={search}
              placeholder="Patient, certificate no, title..."
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Certificate type</label>
            <select
              value={typeFilter}
              onChange={(e) => {
                setPage(1);
                setTypeFilter(e.target.value);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All types</option>
              {templateKeys.map((key) => (
                <option key={key} value={key}>
                  {certificateTemplates[key].label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Rows per page</label>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setPage(1);
                setRowsPerPage(Number(e.target.value));
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ================= TABLE ================= */}
        <div className="bg-white rounded-lg shadow border overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-800 text-white text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Sr No</th>
                <th className="px-4 py-3 text-left">Certificate No</th>
                <th className="px-4 py-3 text-left">Patient</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Issue Date</th>
                <th className="px-4 py-3 text-left">Issued On</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    Loading certificates...
                  </td>
                </tr>
              )}

              {!loading && certificates.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    No certificates issued yet. Click <strong>Generate Certificate</strong> to select a
                    patient and create one.
                  </td>
                </tr>
              )}

              {!loading &&
                certificates.map((c, i) => (
                  <tr key={c._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{(page - 1) * rowsPerPage + i + 1}</td>
                    <td className="px-4 py-3 font-mono text-xs">{c.certificateNo || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{c.patientName}</span>
                      {c.patientMobile && (
                        <span className="block text-xs text-gray-500">{c.patientMobile}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                        {certificateTemplates[c.type]?.label || c.title}
                      </span>
                    </td>
                    <td className="px-4 py-3">{c.fromDate ? formatDate(c.fromDate) : '-'}</td>
                    <td className="px-4 py-3">{formatDate(c.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setViewForm(certificateToForm(c))}
                          className="px-3 py-1.5 text-xs font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 rounded"
                        >
                          View
                        </button>
                        <button
                          onClick={() => openEdit(c)}
                          className="px-3 py-1.5 text-xs font-semibold text-blue-600 border border-blue-600 hover:bg-blue-50 rounded"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDownloadForm(certificateToForm(c))}
                          className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded shadow-sm"
                        >
                          Download
                        </button>
                        <button
                          onClick={() => setShareTarget(c)}
                          className="px-3 py-1.5 text-xs font-semibold text-emerald-700 border border-emerald-600 hover:bg-emerald-50 rounded"
                        >
                          Share
                        </button>
                        <button
                          onClick={() => setDeleteTarget(c)}
                          className="px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-600 hover:bg-red-50 rounded"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* ================= PAGINATION ================= */}
        <div className="flex justify-between items-center mt-4 text-sm">
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((prev) => prev - 1)}
              className="px-3 py-1.5 border rounded disabled:opacity-40 hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => prev + 1)}
              className="px-3 py-1.5 border rounded disabled:opacity-40 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>

        {/* ================= STEP 1 & 2 : PICK PATIENT, THEN TEMPLATE ================= */}
        {pickerOpen && (
          <Modal
            width="max-w-3xl"
            title={pickerStep === 'patient' ? 'Step 1 — Select patient' : 'Step 2 — Select certificate type'}
            onClose={() => setPickerOpen(false)}
          >
            {pickerStep === 'patient' && (
              <>
                <input
                  type="text"
                  autoFocus
                  value={patientSearch}
                  placeholder="Search patient by name, mobile or email..."
                  onChange={(e) => setPatientSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-4 outline-none focus:ring-2 focus:ring-blue-500"
                />

                {patientsLoading && <p className="text-sm text-gray-500 py-4">Searching patients...</p>}

                {!patientsLoading && patients.length === 0 && (
                  <p className="text-sm text-gray-500 py-4">No patients found.</p>
                )}

                <div className="divide-y border rounded-md max-h-80 overflow-y-auto">
                  {patients.map((p) => (
                    <button
                      key={p._id}
                      onClick={() => choosePatient(p)}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 flex justify-between items-center"
                    >
                      <span className="font-medium text-gray-800">
                        {p.firstName} {p.surname || ''}
                      </span>
                      <span className="text-xs text-gray-500">{p.mobile}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {pickerStep === 'type' && pickedCustomer && (
              <>
                <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-md flex justify-between items-center">
                  <div>
                    <p className="text-xs text-gray-500">Selected patient</p>
                    <p className="font-semibold text-gray-800">
                      {pickedCustomer.firstName} {pickedCustomer.surname || ''}
                    </p>
                  </div>
                  <button
                    onClick={() => setPickerStep('patient')}
                    className="px-3 py-1.5 text-xs font-semibold text-blue-600 border border-blue-600 hover:bg-blue-100 rounded"
                  >
                    Change patient
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {templateKeys.map((key) => (
                    <button
                      key={key}
                      onClick={() => chooseTemplate(key)}
                      className="text-left border rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50"
                    >
                      <p className="font-semibold text-gray-800 text-sm">
                        {certificateTemplates[key].label}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{certificateTemplates[key].description}</p>
                    </button>
                  ))}
                </div>
              </>
            )}
          </Modal>
        )}

        {/* ================= EDITOR ================= */}
        {isEditorOpen && (
          <Modal
            title={`${editorMode === 'edit' ? 'Edit' : 'New'} ${form.title}`}
            onClose={() => setForm(null)}
            footer={
              <>
                <button
                  onClick={() => setForm(null)}
                  className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  disabled={saving}
                  onClick={handleSave}
                  className="px-4 py-2 border border-blue-600 text-blue-600 rounded-md text-sm font-medium hover:bg-blue-50 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editorMode === 'edit' ? 'Update' : 'Save'}
                </button>
                <button
                  disabled={saving}
                  onClick={handleSaveAndDownload}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium shadow-sm disabled:opacity-50"
                >
                  Save &amp; Download
                </button>
              </>
            }
          >
            <div className="flex flex-col items-center">
              {/* ================= EDITABLE FIELDS ================= */}
              <div className="w-full max-w-4xl mb-6 border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-bold text-gray-800">Certificate details (editable)</h4>
                  <button
                    onClick={regenerateBody}
                    className="px-3 py-1.5 text-xs font-semibold text-blue-600 border border-blue-600 hover:bg-blue-50 rounded"
                  >
                    Reset text from details
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <EditField label="Certificate Title" value={form.title} onChange={setField('title')} />
                  <EditField label="Patient Name" value={form.patientName} onChange={setField('patientName')} />
                  <EditField label="Clinic Name" value={form.clinicName} onChange={setField('clinicName')} />

                  <EditField label="Doctor Name" value={form.doctorName} onChange={setField('doctorName')} />
                  <EditField label="Qualification" value={form.qualification} onChange={setField('qualification')} />
                  <EditField label="Mobile" value={form.mobile} onChange={setField('mobile')} />

                  <EditField label="Ext." value={form.ext} onChange={setField('ext')} />
                  <EditField label="Timings" value={form.timings} onChange={setField('timings')} />
                  <EditField label="Date (footer)" type="date" value={form.footerDate} onChange={setField('footerDate')} />

                  {/* Template-specific fields */}
                  {(certificateTemplates[form.type]?.fields || []).map((field) => (
                    <EditField
                      key={field}
                      label={FIELD_LABELS[field] || field}
                      type={DATE_FIELDS.includes(field) ? 'date' : 'text'}
                      value={form[field]}
                      onChange={setField(field)}
                    />
                  ))}

                  <div className="md:col-span-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Clinic Address (footer)
                    </label>
                    <input
                      type="text"
                      value={form.address || ''}
                      onChange={(e) => setField('address')(e.target.value)}
                      className="w-full border rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Certificate Text</label>
                    <textarea
                      rows={8}
                      value={form.bodyText || ''}
                      onChange={(e) => setField('bodyText')(e.target.value)}
                      className="w-full border rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500 font-mono"
                    />
                  </div>
                </div>
              </div>

              <CertificateDocument form={form} domId="certificate-preview" />
            </div>
          </Modal>
        )}

        {/* ================= VIEW ================= */}
        {viewForm && (
          <Modal
            title={`${viewForm.title} — ${viewForm.patientName}`}
            onClose={() => setViewForm(null)}
            footer={
              <>
                <button
                  onClick={() => setViewForm(null)}
                  className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => setDownloadForm(viewForm)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium shadow-sm"
                >
                  Download
                </button>
              </>
            }
          >
            <div className="flex justify-center">
              <CertificateDocument form={viewForm} domId="certificate-view" />
            </div>
          </Modal>
        )}

        {/* ================= SHARE ================= */}
        {shareTarget && (
          <Modal
            width="max-w-2xl"
            title={`Share — ${shareTarget.patientName}`}
            onClose={() => setShareTarget(null)}
            footer={
              <button
                onClick={() => setShareTarget(null)}
                className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
            }
          >
            <ShareLinkPanel
              kind="certificate"
              resourceId={shareTarget._id}
              description="open this certificate and download a PDF"
            />
          </Modal>
        )}

        {/* ================= DELETE CONFIRM ================= */}
        {deleteTarget && (
          <Modal
            width="max-w-md"
            title="Delete certificate"
            onClose={() => setDeleteTarget(null)}
            footer={
              <>
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium shadow-sm"
                >
                  Delete
                </button>
              </>
            }
          >
            <p className="text-sm text-gray-700">
              Delete <strong>{deleteTarget.certificateNo}</strong> issued to{' '}
              <strong>{deleteTarget.patientName}</strong>? This removes it from the certificate register.
            </p>
          </Modal>
        )}

        {/* Off-screen render used when downloading straight from the table */}
        {downloadForm && (
          <div style={{ position: 'fixed', top: 0, left: '-10000px', zIndex: -1 }}>
            <CertificateDocument form={downloadForm} ref={downloadRef} domId="certificate-download" />
          </div>
        )}
      </div>
    </RightWrapper>
  );
};

export default Certificates;
